import React, { Component } from 'react'
import { NavDropdown, Modal } from 'react-bootstrap'
import * as Cookies from "es-cookie";
import { setSecureCookie } from "../../../utils/secureCookie";
import { loadStudentSession } from "../../../utils/studentSession";
import gql from "graphql-tag";
import { graphql } from "react-apollo";
import * as compose from 'lodash.flowright';
import './_examswitcher.scss';

const SWITCH_EXAM = gql`
  mutation($student_user_exam_id: Int!, $exam_id: Int!) {
    switchStudentExam(student_user_exam_id: $student_user_exam_id, exam_id: $exam_id) {
      student_user_exam_id
      exam_id
      exam_name
      class_id
    }
  }
`;

// The exams this student is already enrolled in; the menu lists only these.
const FETCH_ENROLLED_EXAMS = gql`
  query($student_user_exam_id: Int!) {
    getStudentEnrolledExams(student_user_exam_id: $student_user_exam_id) {
      student_user_exam_id
      exam_id
      exam_name
    }
  }
`;

// Exams the student can move between from the header, grouped as shown in the
// menu. Enrollments are per-exam, so each has its own student_user_exam_id and
// its own history — switching back returns to the original enrollment rather
// than creating a duplicate. Keep in sync with SWITCHABLE_EXAM_IDS in the
// backend's switchStudentExam.
const EXAM_GROUPS = [
    { label: "National", exams: [{ id: 1, name: "NEET" }, { id: 2, name: "JEE" }] },
    { label: "Andhra Pradesh", exams: [{ id: 3, name: "AP EAPCET (MPC)" }, { id: 7, name: "AP EAPCET (BiPC)" }] },
    { label: "Telangana", exams: [{ id: 6, name: "TS EAMCET (MPC)" }, { id: 8, name: "TS EAMCET (BiPC)" }] },
];
const SWITCHABLE_EXAMS = EXAM_GROUPS.reduce((all, g) => all.concat(g.exams), []);

// Exam switcher (JEE <-> NEET), shared by every page header. Renders nothing for
// students on other streams, where switching is not applicable.
//
// A switch only adds/uses the student's enrollment for that exam
// (student_user_exams); the registered exam in student_users is untouched. So
// the switch lasts for this session (the cookies below) and every new login
// starts on the student's default exam.
class ExamSwitcher extends Component {
    constructor(props) {
        super(props)
        this.state = {
            switchingExam: false,
            showAddExam: false,
            addExamId: null
        }
    }

    // The backend returns the enrollment that is now active. Its id differs per
    // exam and every student-scoped query keys on it, so the cookies must be
    // updated before anything refetches — otherwise the new exam's pages would
    // load with the previous exam's id.
    switchExam = async (examId) => {
        const currentExamId = Cookies.get("examid");
        if (String(examId) === String(currentExamId) || this.state.switchingExam) {
            return;
        }

        this.setState({ switchingExam: true });

        try {
            const res = await this.props.switchStudentExam({
                variables: {
                    student_user_exam_id: parseInt(Cookies.get("student_user_exam_id")),
                    exam_id: parseInt(examId)
                }
            });

            const data = res && res.data && res.data.switchStudentExam;
            if (!data || !data.student_user_exam_id) {
                throw new Error("Exam switch did not return an enrollment");
            }

            setSecureCookie("student_user_exam_id", data.student_user_exam_id);
            setSecureCookie("examid", data.exam_id);
            if (data.exam_name) setSecureCookie("exam_name", data.exam_name);
            if (data.class_id) setSecureCookie("classid", data.class_id);

            // Run what a login runs for the new enrollment: globals, access
            // restrictions, subjects and notifications, stored where the pages
            // read them. Reset the plan selection as login does.
            localStorage.setItem("packageplan", null);
            const loaded = await loadStudentSession(data.student_user_exam_id);

            // Then open home with a full reload, so Apollo's cache and the
            // in-memory stores (which belong to the previous exam) start clean.
            // Not the current page: deep pages (a chapter, an exam result) hold
            // ids that belong to the previous exam. If the session data could
            // not be loaded, the loading screen retries it before going home.
            window.location.href = loaded ? "/student/home" : "/student/loading";
        } catch (error) {
            this.setState({ switchingExam: false });
            console.error("switchExam failed:", error);
            alert("Unable to switch exam right now. Please try again.");
        }
    };

    // Exam ids this student is enrolled in. The current exam always counts,
    // so the menu is never empty while the list is loading or if it fails.
    enrolledExamIds() {
        const ids = new Set([String(Cookies.get("examid"))]);
        const q = this.props.enrolledExams;
        const rows = (q && q.getStudentEnrolledExams) || [];
        rows.forEach(r => ids.add(String(r.exam_id)));
        return ids;
    }

    openAddExam = () => this.setState({ showAddExam: true, addExamId: null });

    closeAddExam = () => {
        if (!this.state.switchingExam) this.setState({ showAddExam: false, addExamId: null });
    };

    // Adding an exam is a switch to it: switchStudentExam creates the
    // enrollment the first time, then the student lands on its home page.
    confirmAddExam = () => {
        if (this.state.addExamId != null) this.switchExam(this.state.addExamId);
    };

    renderAddExamModal(enrolled) {
        const { showAddExam, addExamId, switchingExam } = this.state;
        const groups = EXAM_GROUPS
            .map(g => ({ ...g, exams: g.exams.filter(e => !enrolled.has(String(e.id))) }))
            .filter(g => g.exams.length > 0);

        return (
            <Modal show={showAddExam} onHide={this.closeAddExam} centered className="exam-add-modal" backdropClassName="exam-add-backdrop">
                <Modal.Header closeButton={!switchingExam}>
                    <Modal.Title>Add an exam</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {groups.length === 0 ? (
                        <p className="exam-add-empty">You have already added every exam available.</p>
                    ) : (
                        <>
                            <p className="exam-add-hint">
                                Pick an exam to prepare for. It is added to your account and you can switch back any time.
                            </p>
                            {groups.map(group => (
                                <div key={group.label} className="exam-add-group">
                                    <div className="exam-add-group-label">{group.label}</div>
                                    <div className="exam-add-options">
                                        {group.exams.map(e => (
                                            <button
                                                key={e.id}
                                                type="button"
                                                className={"exam-add-option" + (addExamId === e.id ? " is-selected" : "")}
                                                onClick={() => this.setState({ addExamId: e.id })}
                                                disabled={switchingExam}
                                                aria-pressed={addExamId === e.id}
                                            >
                                                {e.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </Modal.Body>
                {groups.length > 0 && (
                    <Modal.Footer>
                        <button type="button" className="btn btn-light" onClick={this.closeAddExam} disabled={switchingExam}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary exam-add-confirm"
                            onClick={this.confirmAddExam}
                            disabled={addExamId == null || switchingExam}
                        >
                            {switchingExam ? "Adding..." : "Add & switch"}
                        </button>
                    </Modal.Footer>
                )}
            </Modal>
        );
    }

    // The current exam as a pill in the header; the menu lists only the exams
    // the student has added, with a tick on the current one, and "Add exam".
    render() {
        const currentExamId = String(Cookies.get("examid"));
        const current = SWITCHABLE_EXAMS.find(e => String(e.id) === currentExamId);
        if (!current) return null;
        const { switchingExam } = this.state;
        const enrolled = this.enrolledExamIds();
        const canAddMore = SWITCHABLE_EXAMS.some(e => !enrolled.has(String(e.id)));

        return (
            <>
                <NavDropdown
                    id="exam-switch-dropdown"
                    className={"exam-switch mr-3 " + (switchingExam ? "is-switching " : "") + (this.props.className || "")}
                    alignRight
                    disabled={switchingExam}
                    title={
                        <span className="exam-switch-pill" title="Switch exam">
                            <i className={switchingExam ? "far fa-spinner fa-spin" : "far fa-exchange"} />
                            <span className="exam-switch-name">{switchingExam ? "Switching..." : current.name}</span>
                            <i className="far fa-chevron-down exam-switch-caret" />
                        </span>
                    }
                >
                    <div className="exam-switch-menu-title">My exams</div>
                    {EXAM_GROUPS.map(group => {
                        const mine = group.exams.filter(e => enrolled.has(String(e.id)));
                        if (mine.length === 0) return null;
                        return (
                            <React.Fragment key={group.label}>
                                <NavDropdown.Header className="exam-switch-group">{group.label}</NavDropdown.Header>
                                {mine.map(e => {
                                    const isCurrent = String(e.id) === currentExamId;
                                    return (
                                        <NavDropdown.Item
                                            key={e.id}
                                            className={"exam-switch-item" + (isCurrent ? " is-active" : "")}
                                            active={false}
                                            disabled={switchingExam}
                                            onClick={() => this.switchExam(e.id)}
                                        >
                                            <span>{e.name}</span>
                                            {isCurrent ? <i className="far fa-check exam-switch-check" /> : null}
                                        </NavDropdown.Item>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                    {canAddMore && (
                        <>
                            <NavDropdown.Divider />
                            <NavDropdown.Item
                                className="exam-switch-item exam-switch-add"
                                disabled={switchingExam}
                                onClick={this.openAddExam}
                            >
                                <span><i className="far fa-plus mr-2" />Add exam</span>
                            </NavDropdown.Item>
                        </>
                    )}
                </NavDropdown>
                {this.renderAddExamModal(enrolled)}
            </>
        );
    }
}

export default compose(
    graphql(SWITCH_EXAM, { name: "switchStudentExam" }),
    graphql(FETCH_ENROLLED_EXAMS, {
        name: "enrolledExams",
        skip: () => !Cookies.get("student_user_exam_id"),
        options: () => ({
            variables: { student_user_exam_id: parseInt(Cookies.get("student_user_exam_id")) },
            fetchPolicy: "cache-and-network",
        }),
    })
)(ExamSwitcher);
